// FILE: src/Components/CreateGroup/CreateBrandWorkflowModal.js (FULLY UPDATED)
import React, { useState, useEffect } from 'react';
import { Modal, message, Input, Button, Progress } from 'antd';
import { useDispatch } from 'react-redux';
import { createGroup } from '../../services/user.profile';
import { checkOnboardingStatus } from '../../services/group';
import UpgradeModal from '../BuySubscription/UpgradeModal';
import { fetchGroups } from '../../store/Slices/GroupSlice';
import { LoadingOutlined } from '@ant-design/icons';

const BrandProcessingScreen = ({ statusText, progress = 0 }) => (
  <div className="py-8 px-4 text-center">
    <div className="relative w-20 h-20 mx-auto mb-6">
      <div className="absolute inset-0 bg-purple-100 rounded-full animate-ping opacity-75"></div>
      <div className="relative bg-white p-5 rounded-full shadow-xl border border-purple-50 flex items-center justify-center h-full w-full">
        <LoadingOutlined style={{ fontSize: 32, color: '#7C3AED' }} spin />
      </div>
    </div>

    <h3 className="text-2xl font-bold text-gray-900 mb-2">Creating your brand…</h3>
    <p className="text-sm font-medium text-purple-600 mb-5 bg-purple-50 inline-block px-4 py-2 rounded-full">
      {statusText}
    </p>

    <div className="max-w-md mx-auto">
      <Progress
        percent={Math.round(progress)}
        strokeColor="#7C3AED"
        trailColor="#E9D5FF"
        showInfo={false}
      />
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        <span>{Math.round(progress)}%</span>
        <span>Usually takes about 52 seconds</span>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-2 max-w-md mx-auto mt-6 text-left">
      {[
        'Scanning website',
        'Extracting logos',
        'Detecting colors',
        'Preparing brand kit',
      ].map((item, idx) => (
        <div
          key={idx}
          className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
        >
          <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></span>
          {item}
        </div>
      ))}
    </div>

    <p className="text-xs text-gray-400 mt-5">Please keep this window open.</p>
  </div>
);

export default function CreateBrandWorkflowModal({
  isOpen,
  onClose,
  onBrandCreateComplete,
}) {
  const [view, setView] = useState('form');
  const [brandName, setBrandName] = useState('');
  const [website, setWebsite] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pollingGroupId, setPollingGroupId] = useState(null);
  const [processingMessage, setProcessingMessage] = useState('Initializing AI Agents...');
  const [progress, setProgress] = useState(0);
  const [isBackendDone, setIsBackendDone] = useState(false);

  const [upgradeModal, setUpgradeModal] = useState({
    visible: false,
    message: '',
    featureName: '',
  });

  const dispatch = useDispatch();

  useEffect(() => {
    let progressInterval;

    if (view === 'loading') {
      setProgress(0);
      setIsBackendDone(false);

      progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (isBackendDone) {
            return prev >= 100 ? 100 : Math.min(prev + 8, 100);
          }

          if (prev < 15) return prev + 3;
          if (prev < 40) return prev + 2;
          if (prev < 65) return prev + 1.4;
          if (prev < 85) return prev + 0.9;
          if (prev < 92) return prev + 0.35;
          return prev;
        });
      }, 1000);
    }

    return () => clearInterval(progressInterval);
  }, [view, isBackendDone]);

  useEffect(() => {
    if (!pollingGroupId || view !== 'loading') return;

    let pollingInterval;
    let messageInterval;
    let isMounted = true;

    const handleJobCompletion = (status) => {
      if (!isMounted) return;

      setIsBackendDone(true);
      setProgress((prev) => (prev < 95 ? 95 : prev));

      setTimeout(() => {
        if (!isMounted) return;
        setProgress(100);

        setTimeout(() => {
          if (!isMounted) return;
          onBrandCreateComplete(brandName, status);
        }, 700);
      }, 1200);
    };

    const pollStatus = async () => {
      try {
        const result = await checkOnboardingStatus(pollingGroupId);

        if (!isMounted) return;

        if (result.status === 'completed' || result.status === 'failed') {
          clearInterval(pollingInterval);
          clearInterval(messageInterval);
          handleJobCompletion(result.status);
        }
      } catch (error) {
        console.error('Polling error in modal:', error);
        clearInterval(pollingInterval);
        clearInterval(messageInterval);

        if (isMounted) {
          message.error('Could not check brand status. Please try again from the Brand Kit page.');
          handleCancel();
        }
      }
    };

    const messages = [
      'Scanning website...',
      'Extracting assets...',
      'Analyzing colors...',
      'Finalizing DNA...',
    ];
    let msgIndex = 0;

    messageInterval = setInterval(() => {
      if (isMounted) {
        setProcessingMessage(messages[msgIndex % messages.length]);
        msgIndex++;
      }
    }, 4000);

    pollStatus();
    pollingInterval = setInterval(pollStatus, 5000);

    return () => {
      isMounted = false;
      clearInterval(pollingInterval);
      clearInterval(messageInterval);
    };
  }, [pollingGroupId, view, brandName, onBrandCreateComplete]);

  const handleCreateBrand = async () => {
    if (!brandName.trim() || !website.trim()) {
      message.error('Brand Name and Website are required.');
      return;
    }

    if (!website.includes('.')) {
      message.error('Please enter a valid website URL.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await createGroup({
        groupName: brandName,
        website: website,
      });

      if (response && response.success && response.group?._id) {
        dispatch(fetchGroups());
        setPollingGroupId(response.group._id);
        setView('loading');
      } else {
        message.error(response.error || 'Failed to create brand.');
      }
    } catch (error) {
      if (error.response?.data?.errorType === 'LIMIT_REACHED') {
        setUpgradeModal({
          visible: true,
          message: error.response.data.message,
          featureName: 'Brands',
        });
      } else {
        message.error(error.message || 'An error occurred during brand creation.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setBrandName('');
    setWebsite('');
    setView('form');
    setPollingGroupId(null);
    setProgress(0);
    setIsBackendDone(false);
    onClose();
  };

  return (
    <>
      <Modal
        title={view === 'form' ? 'Create a New Brand' : 'Analyzing Your Brand'}
        open={isOpen}
        onCancel={handleCancel}
        footer={
          view === 'form'
            ? [
                <Button key="back" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  loading={isLoading}
                  onClick={handleCreateBrand}
                >
                  Create & Analyze
                </Button>,
              ]
            : null
        }
        closable={view === 'form'}
        maskClosable={view === 'form'}
        centered
        width={700}
      >
        {view === 'form' ? (
          <div className="p-4 space-y-4">
            <p className="text-gray-600">
              Enter your new brand&apos;s name and website. The AI will analyze it to
              automatically build your brand kit.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Name
              </label>
              <Input
                placeholder="e.g., Oolook"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand Website
              </label>
              <Input
                placeholder="e.g., https://oolook.in"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <BrandProcessingScreen
            statusText={processingMessage}
            progress={progress}
          />
        )}
      </Modal>

      <UpgradeModal
        isOpen={upgradeModal.visible}
        onClose={() =>
          setUpgradeModal({ visible: false, message: '', featureName: '' })
        }
        message={upgradeModal.message}
        featureName={upgradeModal.featureName}
      />
    </>
  );
}